/** biome-ignore-all lint/suspicious/noArrayIndexKey: allow array index as key */
import cx from 'classnames';
import {type KeyboardEventHandler, memo, useCallback} from 'react';
import {useHistory} from 'react-router-dom';
import {AmazonBanner} from '~/ViewInvitation/components/Guestlist/AmazonBanner';
import {Avatar} from '~/ViewInvitation/components/Guestlist/Avatar';
import {useGuestlistDataContext} from '~/ViewInvitation/components/Guestlist/hooks/useGuestlistDataContext';
import {Details} from '~/ViewInvitation/components/Guestlist/List/Details';
import {DeliveryStatus, type Guest, isHostGuest} from '~/ViewInvitation/types/guests';
import {ChevronIcon} from '~library/svg/ChevronIcon';
import {EditLineIcon} from '~library/svg/v2/EditLineIcon';

import * as styles from './Row.module.scss';

interface RowProps {
  guestIndex: number;
  guest: Guest;
}

export const Row = memo(({guest, guestIndex}: RowProps) => {
  const history = useHistory();
  const {
    refetch,
    state: {rsvpResponse},
  } = useGuestlistDataContext();

  const onGuestActionClick = useCallback(() => {
    if (!guest.userId) {
      // if the event was just created, sometimes the guests don't have IDs yet, so we need to refetch
      // ideally we should want to still perform this action after the ID gets loaded in, but that can be a future refactor
      refetch();
    } else if (isHostGuest(guest) && guest.deliveryStatus === DeliveryStatus.FAILED) {
      history.push({pathname: `/guests/${guest.userId}/update`, search: history.location.search});
    } else {
      history.push({pathname: `/guests/${guest.userId}`, search: history.location.search});
    }
  }, [guest, history, refetch]);

  const onGuestActionKeyUp: KeyboardEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === 'Space') onGuestActionClick();
    },
    [onGuestActionClick]
  );

  return (
    <>
      <div
        className={cx(styles.row, {[styles.rowLoading]: !guest.userId})}
        onClick={onGuestActionClick}
        data-qa-id={`guest-action-${guest.userId}`}
        data-qa-value={guest.userId}
        onKeyUp={onGuestActionKeyUp}
        role="button"
        tabIndex={0}
        title={guest.userId ? 'View guest' : 'Guest is still being added, please wait'}
      >
        <div className={styles.avatar}>
          <Avatar guest={guest} />
        </div>
        <Details guest={guest} />
        <div
          className={cx(styles.actions, {
            [styles.editButton]:
              isHostGuest(guest) && guest.deliveryStatus === DeliveryStatus.FAILED,
          })}
        >
          {isHostGuest(guest) && guest.deliveryStatus === DeliveryStatus.FAILED && (
            <EditLineIcon ratio={0.5625} />
          )}
          {isHostGuest(guest) && guest.deliveryStatus !== DeliveryStatus.FAILED && (
            <ChevronIcon direction="right" ratio={0.75} />
          )}
        </div>
      </div>
      {rsvpResponse === undefined && guestIndex === 2 && <AmazonBanner />}
      {rsvpResponse !== undefined && guestIndex === 9 && <AmazonBanner />}
    </>
  );
});

Row.displayName = 'Row';
